// Date utility functions for the challenge tracker

/**
 * Add days to a date
 */
export const addDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

/**
 * Format date as YYYY-MM-DD
 */
export const formatDate = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Format date for display (e.g., "Jan 15")
 */
export const formatDisplayDate = (date) => {
  const d = new Date(date);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[d.getMonth()]} ${d.getDate()}`;
};

/**
 * Check if a date is today
 */
export const isToday = (date) => {
  const today = new Date();
  const checkDate = new Date(date);
  return formatDate(today) === formatDate(checkDate);
};

/**
 * Check if a date is in the past
 */
export const isPast = (date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const checkDate = new Date(date);
  checkDate.setHours(0, 0, 0, 0);
  return checkDate < today;
};

/**
 * Check if a date is in the future
 */
export const isFuture = (date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const checkDate = new Date(date);
  checkDate.setHours(0, 0, 0, 0);
  return checkDate > today;
};

/**
 * Calculate days between two dates
 */
export const daysBetween = (date1, date2) => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  d1.setHours(0, 0, 0, 0);
  d2.setHours(0, 0, 0, 0);
  const diffTime = Math.abs(d2 - d1);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

/**
 * Get the day of week (0-6, Sunday-Saturday)
 */
export const getDayOfWeek = (date) => {
  return new Date(date).getDay();
};

/**
 * Get day name (e.g., "Monday")
 */
export const getDayName = (date) => {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return days[getDayOfWeek(date)];
};

/**
 * Generate all dates for the challenge
 */
export const generateChallengeDates = (startDate, numberOfDays) => {
  const dates = [];
  const start = new Date(startDate);
  
  for (let i = 0; i < numberOfDays; i++) {
    const date = addDays(start, i);
    dates.push({
      dayNumber: i + 1,
      date: formatDate(date),
      displayDate: formatDisplayDate(date),
      dayName: getDayName(date),
      isToday: isToday(date),
      isPast: isPast(date),
      isFuture: isFuture(date)
    });
  }
  
  return dates;
};

/**
 * Calculate current streak
 */
export const calculateStreak = (completedDays, allDates) => {
  let streak = 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  // Start from today and go backwards
  for (let i = allDates.length - 1; i >= 0; i--) {
    const date = new Date(allDates[i].date);
    date.setHours(0, 0, 0, 0);
    
    // Only count days up to today
    if (date > today) continue;
    
    if (completedDays.has(allDates[i].date)) {
      streak++;
    } else {
      break;
    }
  }
  
  return streak;
};

/**
 * Calculate best streak
 */
export const calculateBestStreak = (completedDays, allDates) => {
  let bestStreak = 0;
  let currentStreak = 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  for (let i = 0; i < allDates.length; i++) {
    const date = new Date(allDates[i].date);
    date.setHours(0, 0, 0, 0);
    
    // Only count days up to today
    if (date > today) break;
    
    if (completedDays.has(allDates[i].date)) {
      currentStreak++;
      bestStreak = Math.max(bestStreak, currentStreak);
    } else {
      currentStreak = 0;
    }
  }
  
  return bestStreak;
};